import * as classnames from 'classnames'
import { cloneDeep, first, isEmpty, isEqual, remove, round, set, uniqBy, reverse } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs'
import useForm from '@teqplay/react-useform'
import produce from 'immer'
import { v4 as uuidV4 } from 'uuid'
import { toast } from 'react-toastify'

import { QUAY_ROLES, startDateFormatString } from '../../js/constants'
import { handleFetchError, sendMessageToSentry } from '../../js/sentry'
import {
  validateTerminalVisitProperties,
  validateTerminalVisitOrders,
  updateVisitsWithFormFields,
  getErrorMessageForFormField
} from '../../utils/formUtils'
import { findOrderByUuid, getAllProductsAtQuays, isEtaStillValid } from '../../utils/general'
import { getConflictsForOneTerminalVisit } from '../../utils/terminalVisitConflictsUtils'
import {
  checkChainCargo,
  clearAtaEtaAtd,
  determineShipLength,
  determineTotalDurationDelays,
  hasParallelOrders,
  syncParallelOrders,
  updateCargoEnd,
  updateShipLength,
  updateDefaultSlack,
  getClosestQuayToBollard,
  calculateOptimalBollardPositions,
  updateBollardEnd,
  checkChainBollard,
  isVisitInNormalQuay
} from '../../utils/terminalVisitUtils'
import ErrorBoundary from '../errorBoundary/ErrorBoundary'
import ErrorMessage from '../errormessage/ErrorMessage'
import CheckBoxField from '../fields/checkboxfield/CheckBoxField'
import DisplayField from '../fields/displayfield/DisplayField'
import InputDateField from '../fields/inputdatefield/InputDateField'
import InputNumberField from '../fields/inputnumberfield/InputNumberField'
import InputSelectField from '../fields/inputselectfield/InputSelectField'
import InputTextField from '../fields/inputtextfield/InputTextField'
import ConflictDetails from './conflictdetails/ConflictDetails'
import OrderForm from './orderForm/OrderForm'
import ShipSelector from './shipselector/ShipSelector'
import './TerminalVisitForm.scss'
import { confirmDeletePopup } from '../popup/confirmDeletePopup'
import Modal from '../app/customModal/Modal'
import { categorizeVisits, isVisitOnExternalQuay } from '../../utils/terminalPlannerUtils'
import { userPermissionRoles, hasBoardBoardSupport } from '../../utils/userPermissions'

// Backend expects demurrage and delay orderId to be null if related to the whole visit
const terminalVisitDropdownOption = { displayText: 'Terminal Visit', value: null }

/**
 * Determine for every order whether it has initially chained cargo
 * @param {TerminalVisit} terminalVisit
 * @param {Config} config
 */
const initOrdersChainCargo = (terminalVisit, config) => {
  const ordersChainCargo = {}
  const totalDurationDelays = determineTotalDurationDelays(terminalVisit)

  terminalVisit.orders.forEach(order => {
    ordersChainCargo[order.uuid] = checkChainCargo(order, config, totalDurationDelays)
  })

  return ordersChainCargo
}
const initOrdersChainBollards = (visit, config) => {
  return visit.orders
    .map(order => {
      return checkChainBollard(order, visit.ship, config) ? null : order.uuid
    })
    .filter(id => id)
}

function TerminalVisitForm(props) {
  const [preStatusChange, setPreStatusChange] = useState({ status: null, terminalVisit: null })
  const [loadingShipDetails, setLoadingShipDetails] = useState(false)
  const [shipDetailsForVisit, setShipDetailsForVisit] = useState({ mmsi: '' })
  const [ordersChainCargo, setOrdersChainCargo] = useState(initOrdersChainCargo(props.selectedVisit, props.config))
  const [inactiveOrdersChainBollard, setInactiveOrdersChainBollard] = useState(
    initOrdersChainBollards(props.selectedVisit, props.config)
  )
  const [parallelOrders, setParallelOrders] = useState(hasParallelOrders(props.selectedVisit))
  const [hasBeenValidatedOnce, setHasBeenValidatedOnce] = useState(false)

  const { values, errors, handleSubmit, setAllValues, triggerValidation } = useForm({
    initialValues: props.selectedVisit,
    validate,
    onValidSubmit
  })

  const { selectedVisit, config, getShipDetailsByMmsi } = props

  useEffect(() => {
    async function fetchDetailsSelectedShip() {
      // Fetch shipDetails for the ship that belongs to the selected visit
      const currentShipMmsiForVisit = selectedVisit.ship ? selectedVisit.ship.mmsi : null
      if (currentShipMmsiForVisit) {
        setLoadingShipDetails(true)
        try {
          const ship = await getShipDetailsByMmsi(currentShipMmsiForVisit)
          setLoadingShipDetails(false)
          setShipDetailsForVisit(ship)
        } catch (err) {
          setLoadingShipDetails(false)
          if (err.status !== 404) {
            handleFetchError(err)
          }
        }
      }
    }
    fetchDetailsSelectedShip()
  }, [selectedVisit, config, getShipDetailsByMmsi])

  useEffect(() => {
    if (hasBeenValidatedOnce) {
      triggerValidation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  /**
   * Props.selectedVisit changes when 'Save and copy' is clicked.
   */
  if (!isEqual(props.selectedVisit._id, values._id)) {
    setAllValues(cloneDeep(props.selectedVisit))
    setOrdersChainCargo(initOrdersChainCargo(props.selectedVisit, props.config))
    setInactiveOrdersChainBollard(initOrdersChainBollards(props.selectedVisit, props.config))
    setParallelOrders(hasParallelOrders(props.selectedVisit))
  }

  const handleAddDemurrage = e => {
    e.preventDefault()
    const formFieldsCopy = cloneDeep(values)
    // set empty orderId by default, this indicates that demurrage applies to entire visit
    formFieldsCopy.demurrages = (formFieldsCopy.demurrages || []).concat({ orderId: terminalVisitDropdownOption.value })
    setAllValues(formFieldsCopy)
  }

  // determine roles
  const { planner: isPlanner, orderAdmin: isOrderAdmin, quayCoordinator: isQuayCoordinator } = userPermissionRoles(
    props.userRoles
  )

  // determine permissions
  const canEditOrderDetails = isPlanner
  const canEditAdministrativeDetails = isPlanner || isOrderAdmin
  const canEditCargoOperationsDetails = isPlanner || isQuayCoordinator
  const canEditTerminalVisit = isPlanner
  const canEditBollards = isQuayCoordinator || isPlanner

  // If there is no visit id -> we are creating a new visit
  const creatingNewVisit = !values._id

  const selectedOrder = findSelectedOrder(values)
  const selectedOrderIndex = values.orders.indexOf(selectedOrder)

  const terminalHasExternalQuay = props.config.quays.some(quay => quay.role === QUAY_ROLES.external)
  const isOnExternalQuay = isVisitOnExternalQuay(props.config.quays, values.quayId)

  const showDemurrageWarning = isVisitIncurringDemurrage()
  const isValidForm = isEmpty(validateTerminalVisitForm(values, isOnExternalQuay))

  const quayNames = props.config.quays.map(quay => {
    return quay.name
  })

  // make a division in the visits which are on the external quay or on the mainQuay
  const { externalQuayVisits, mainQuayVisits } = categorizeVisits(props.config.quays, props.terminalVisits)

  const updatedVisits = isValidForm ? updateVisitsWithFormFields(mainQuayVisits, values) : mainQuayVisits

  // TODO: instead of calculating the conflicts again, get them passed from TerminalViewer?
  const conflicts = getConflictsForOneTerminalVisit(
    updatedVisits,
    values,
    selectedOrder,
    props.config.quays,
    props.config.etaTooLateThreshold,
    props.config.planningDelayTolerance
  )

  // TODO: move this into config and add a field for it in the configuration UI
  const boardBoardSupport = hasBoardBoardSupport(props.config.terminalId)

  // cargo and amount is optional in case of a boardBoard operation
  const cargoRequired = boardBoardSupport ? !values.boardBoard : true

  const showDelays = values.status !== 'planned'
  const hasMultipleOrders = values.orders.length > 1
  const totalDurationDelays = determineTotalDurationDelays(values)

  const tabsClassName = classnames('react-tabs', {
    'single-order': !hasMultipleOrders,
    'multiple-orders': hasMultipleOrders
  })

  const dropDownOrderOptions = !hasMultipleOrders
    ? [terminalVisitDropdownOption]
    : [terminalVisitDropdownOption].concat(
        values.orders.map((order, index) => ({ displayText: `Order ${index + 1}`, value: order.uuid }))
      )

  const demurrageTypeOptions = ['NONE', 'LOADING', 'DISCHARGE', 'CLEANING']
  const orders = values.orders

  const isParallelVisit = values.parallelGroups > 1
  const showOperationSides = isParallelVisit && values.orders.length > 2

  return (
    <ErrorBoundary>
      <React.Fragment>
        <div className="terminal-visit-form terminal-visit-form-wrapper">
          <div className="stretch-wrapper">
            <div className="content order-wrapper">
              <Tabs selectedIndex={selectedOrderIndex} onSelect={handleSelectOrder} className={tabsClassName}>
                <TabList>
                  {values.orders.map((order, orderIndex) => {
                    const showLeftArrow =
                      orders.length > 1 && orderIndex > 0 && selectedOrderIndex === orderIndex ? true : false
                    const showRightArrow =
                      orders.length > 1 && orderIndex < orders.length - 1 && selectedOrderIndex === orderIndex
                        ? true
                        : false
                    const enableSwitch = isParallelVisit && orders.length === 2
                    return (
                      <Tab key={order.uuid || orderIndex}>
                        {showLeftArrow && (
                          <i
                            className="fa fa-arrow-left reorder-left"
                            onClick={() => handleChangeOrdersOrder(orders, orderIndex, orderIndex - 1, enableSwitch)}
                          />
                        )}
                        Order {orderIndex + 1}
                        {showRightArrow && (
                          <i
                            className="fa fa-arrow-right reorder-right"
                            onClick={() => handleChangeOrdersOrder(orders, orderIndex, orderIndex + 1, enableSwitch)}
                          />
                        )}
                      </Tab>
                    )
                  })}
                </TabList>
                <div className="add-order-button" onClick={addNewOrder}>
                  + Add Order
                </div>

                {values.orders.map((order, orderIndex) => {
                  const chainCargo = ordersChainCargo[order.uuid]
                  const chainBollard = !inactiveOrdersChainBollard.includes(order.uuid)
                  return (
                    <TabPanel key={order.uuid || orderIndex}>
                      <OrderForm
                        config={props.config}
                        terminalVisitId={values._id}
                        order={order}
                        errors={errors && errors.orders && errors.orders[orderIndex] ? errors.orders[orderIndex] : null}
                        ship={values.ship}
                        chainCargo={chainCargo}
                        chainBollard={chainBollard}
                        cargoRequired={cargoRequired}
                        parallelOrders={parallelOrders}
                        totalDurationDelays={totalDurationDelays}
                        onChange={handleChangeOrder}
                        onChangeCargo={handleChangeCargo}
                        onChainCargo={handleChainCargo}
                        onChainBollard={handleChainBollard}
                        readOnly={{
                          orderDetails: !canEditOrderDetails,
                          administrativeDetails: !canEditAdministrativeDetails,
                          cargoOperationsDetails: !canEditCargoOperationsDetails,
                          bollardPosition: !canEditBollards
                        }}
                        authToken={props.authToken}
                        customFetch={props.customFetch}
                        isCollapsedVisit={isOnExternalQuay}
                        showOperationSides={showOperationSides}
                        isParallelVisit={isParallelVisit}
                        ordersFormFields={values.orders}
                      />
                    </TabPanel>
                  )
                })}
              </Tabs>
            </div>

            <div className="ship-visit-wrapper content side">
              {showDemurrageWarning && (
                <div className="detail-view-section warning">This visit is incurring demurrage</div>
              )}
              <ShipSelector
                ship={values.ship ? values.ship : {}}
                shipDetailsForVisit={shipDetailsForVisit || {}}
                onChangeShip={setShip}
                onChangeShipProperty={setShipProperty}
                errors={errors && errors.ship ? errors.ship : null}
                config={props.config}
                loadingShipsDetails={loadingShipDetails}
                authToken={props.authToken}
                customFetch={props.customFetch}
                readOnly={!canEditTerminalVisit}
                lockInformation={values.lockInformation || {}}
              />

              {!isOnExternalQuay && <ConflictDetails conflicts={conflicts} />}

              {terminalHasExternalQuay && (
                <>
                  <div className="detail-view-section">
                    <h1>Quay</h1>

                    <InputSelectField
                      fieldName={'quay'}
                      fieldValue={values.quayId}
                      onChange={option => {
                        handleChangeQuay(option)
                      }}
                      options={quayNames}
                      readOnly={!canEditTerminalVisit}
                    />
                  </div>

                  {!isOnExternalQuay && (
                    <div className="detail-view-section">
                      <h1>Link to External</h1>
                      <InputSelectField
                        fieldName={'dependsOnVisit'}
                        fieldValue={values.dependsOnVisit}
                        onChange={option => {
                          onFormInputChange(option, 'dependsOnVisit')
                        }}
                        options={externalQuayVisits.map(externalVisit => {
                          const visitDate = moment(externalVisit.orders[0].cargoStart).format('DD/MM/YYYY')
                          const visitHasShipName =
                            externalVisit.ship && externalVisit.ship.name && externalVisit.ship.name !== ''
                              ? true
                              : false

                          return {
                            displayText: visitHasShipName
                              ? `${externalVisit.ship.name} - ${visitDate}`
                              : `Unkown Ship - ${visitDate}`,
                            value: externalVisit._id
                          }
                        })}
                        readOnly={!canEditTerminalVisit}
                        emptyOption
                      />
                    </div>
                  )}
                </>
              )}

              <div className="detail-view-section">
                <h1>Operational Status</h1>
                <div className="detailview triple-view">
                  <InputSelectField
                    title={'Visit status'}
                    fieldName={'status'}
                    fieldValue={values.status}
                    onChange={onStatusInputChange}
                    options={['planned', 'assigned', 'nearby', 'moored', 'sailed']}
                    setupValue={'planned'}
                    previousStatus={preStatusChange.status}
                    readOnly={!canEditTerminalVisit}
                  />
                  {(values.status === 'planned' || values.status === 'assigned') && (
                    <DisplayField
                      title={'ETA'}
                      value={
                        values.etaTime
                          ? isEtaStillValid(values.etaTime, values.etaTimeUpdatedAt)
                            ? moment(values.etaTime).format(startDateFormatString) +
                              ' - ' +
                              moment(values.etaTime).fromNow()
                            : 'Outdated'
                          : null
                      }
                    />
                  )}
                  {values.status === 'nearby' && <DisplayField title={'ETA'} value={'Any moment'} />}
                  {(values.status === 'moored' || values.status === 'sailed') && (
                    <InputDateField
                      name={'ATA'}
                      fieldName={'ataTime'}
                      fieldValue={values.ataTime}
                      required={true}
                      error={getErrorMessageForFormField(errors, 'ataTime')}
                      onChange={onFormInputChange}
                      pickerDisplayTop
                      readOnly={!canEditTerminalVisit}
                    />
                  )}
                  {values.status !== 'sailed' ? (
                    <DisplayField
                      title={'ATD'}
                      value={values.atdTime ? moment(values.atdTime).format(startDateFormatString) : null}
                    />
                  ) : (
                    <InputDateField
                      name={'ATD'}
                      fieldName={'atdTime'}
                      fieldValue={values.atdTime}
                      required={true}
                      error={getErrorMessageForFormField(errors, 'atdTime')}
                      onChange={onFormInputChange}
                      pickerDisplayTop
                      pickerAlignLeft
                      readOnly={!canEditTerminalVisit}
                    />
                  )}
                </div>
                <div>
                  {boardBoardSupport ? (
                    <div className="detailview">
                      <CheckBoxField
                        name="Board-board operation"
                        fieldName="boardBoard"
                        fieldValue={values.boardBoard}
                        onChange={onFormInputChange}
                        readOnly={!canEditOrderDetails}
                      />
                    </div>
                  ) : null}
                </div>

                {hasMultipleOrders ? (
                  <div className="detail-view-section">
                    <h1>Parallel operations</h1>
                    <div className="detailview triple-view">
                      <CheckBoxField
                        name="Make parallel visit"
                        fieldName="isParallelVisit"
                        fieldValue={isParallelVisit}
                        onChange={toggleIsParallelVisit}
                        readOnly={!canEditOrderDetails}
                      />
                      {/* TODO see how the class .group styles work so  <div></div> is not necessary*/}

                      <div></div>
                    </div>
                  </div>
                ) : null}
              </div>

              {showDelays ? (
                <div className="detail-view-section">
                  <h1>Delays</h1>
                  <div className="dynamic-items-wrapper">
                    {hasMultipleOrders && !isEmpty(values.delays) ? (
                      <div className="info">
                        Because this visit has multiple orders, delays will not be accounted for in the calculated cargo
                        end time.
                      </div>
                    ) : null}
                    {values.delays &&
                      values.delays.map((delay, index) => {
                        return (
                          <div key={index} className="dynamic-item-wrapper">
                            <div className="dynamic-item">
                              <div className="detailview">
                                <InputSelectField
                                  title={'Related to'}
                                  fieldName={'orderId'}
                                  fieldValue={delay.orderId}
                                  onChange={(value, field) => handleDelayInputChange(value, field, index)}
                                  options={dropDownOrderOptions}
                                  readOnly={!canEditTerminalVisit || !hasMultipleOrders}
                                />
                              </div>
                              <div className="detailview triple-view">
                                <InputSelectField
                                  title={'Type'}
                                  fieldName={'delayType'}
                                  fieldValue={delay.delayType}
                                  onChange={(value, field) => handleDelayInputChange(value, field, index)}
                                  readOnly={!canEditTerminalVisit}
                                  options={[
                                    'RAIN',
                                    'MANPOWER',
                                    'TECHNICAL',
                                    'EXTERNAL_ANALYSIS',
                                    'INTERNAL_ANALYSIS',
                                    'FOOD_SAFETY',
                                    'OUT_OF_SPECS',
                                    'SURVEYOR',
                                    'OTHER'
                                  ]}
                                />
                                <InputDateField
                                  name={'Start time'}
                                  fieldName={'startTime'}
                                  fieldValue={delay.startTime}
                                  error={getErrorMessageForFormField(errors, `delays[${index}].startTime`)}
                                  required={false}
                                  readOnly={!canEditTerminalVisit}
                                  onChange={(value, field) => handleDelayInputChange(value, field, index)}
                                />
                                <InputDateField
                                  name={'End time'}
                                  fieldName={'endTime'}
                                  fieldValue={delay.endTime}
                                  error={getErrorMessageForFormField(errors, `delays[${index}].endTime`)}
                                  required={false}
                                  readOnly={!canEditTerminalVisit}
                                  pickerAlignLeft
                                  onChange={(value, field) => handleDelayInputChange(value, field, index)}
                                />
                              </div>
                              <div className="detailview">
                                <InputTextField
                                  name={'Comment'}
                                  fieldName={'comment'}
                                  fieldValue={delay.comment}
                                  error={false}
                                  required={false}
                                  readOnly={!canEditTerminalVisit}
                                  onChange={(value, field) => handleDelayInputChange(value, field, index)}
                                />
                              </div>
                            </div>
                            <div className="item-delete-button">
                              {canEditTerminalVisit && (
                                <div className="delete-btn btn" onClick={() => handleDeleteDelay(index)}>
                                  <span>Delete</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    <div className="add-dynamic-item-wrapper">
                      {canEditTerminalVisit && (
                        <div className="yes-btn btn" onClick={handleAddDelay}>
                          <span>Add delay</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="detail-view-section">
                <h1>Demurrages</h1>
                <div className="dynamic-items-wrapper">
                  {values.demurrages &&
                    values.demurrages.map((demurrage, index) => {
                      return (
                        <div key={index} className="dynamic-item-wrapper">
                          <div className="dynamic-item">
                            <div className="detailview triple-view demurrages">
                              <InputSelectField
                                title={'Related to'}
                                fieldName={'orderId'}
                                fieldValue={demurrage.orderId}
                                onChange={(value, field) => handleDemurrageInputChanged(value, field, index)}
                                options={dropDownOrderOptions}
                                readOnly={!canEditTerminalVisit || !hasMultipleOrders}
                              />
                              <InputSelectField
                                title={'Demurrage type'}
                                fieldName={'type'}
                                fieldValue={demurrage.type}
                                onChange={(value, field) => handleDemurrageInputChanged(value, field, index)}
                                options={demurrageTypeOptions.map(demurrageType => {
                                  return { displayText: demurrageType.toLowerCase(), value: demurrageType }
                                })}
                                readOnly={!canEditTerminalVisit}
                              />
                              <InputNumberField
                                name="Allowed duration (hours)"
                                step={0.01}
                                fieldName={'allowedDuration'}
                                required={false}
                                fieldValue={
                                  demurrage.allowedDuration
                                    ? round(demurrage.allowedDuration / (60 * 60 * 1000), 2)
                                    : null
                                }
                                error={getErrorMessageForFormField(errors, `demurrages[${index}].allowedDuration`)}
                                onChange={(value, field) => handleDemurrageInputChanged(value, field, index)}
                                readOnly={!canEditTerminalVisit}
                              />
                              <InputNumberField
                                name={`Total charges (${demurrage.demurrageCurrency || 'EUR'})`}
                                step={0.01}
                                required={false}
                                error={getErrorMessageForFormField(errors, `demurrages[${index}].demurrageInCents`)}
                                fieldName={'demurrageInCents'}
                                fieldValue={demurrage.demurrageInCents ? demurrage.demurrageInCents / 100 : null}
                                onChange={(value, field) => handleDemurrageInputChanged(value, field, index)}
                                readOnly={!canEditTerminalVisit}
                              />
                            </div>
                          </div>
                          <div className="item-delete-button">
                            {canEditTerminalVisit && (
                              <div className="delete-btn btn" onClick={() => handleDeleteDemurrage(index)}>
                                <span>Delete</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  <div className="add-dynamic-item-wrapper">
                    {canEditTerminalVisit && (
                      <div className="yes-btn btn" onClick={handleAddDemurrage}>
                        <span>Add demurrage</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="detail-view-section">
                <h1>IMOS</h1>
                <div className="dynamic-items-wrapper"></div>
                <CheckBoxField
                  name={'Ignore IMOS updates'}
                  fieldName={'ignoreIMOS'}
                  fieldValue={values.ignoreIMOS}
                  onChange={onFormInputChange}
                  readOnly={
                    !canEditTerminalVisit ||
                    creatingNewVisit ||
                    (typeof values.externalId === 'undefined' && values.ignoreIMOS)
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <div className="footer">
          {!isEmpty(errors) ? (
            <ErrorMessage message={'Please make sure that you filled in all fields correctly'} />
          ) : null}

          <Modal.Buttons>
            {canEditTerminalVisit && !creatingNewVisit ? (
              <div className="no-btn btn" onClick={deleteTerminalVisit}>
                <span>{hasMultipleOrders ? `Delete Order ${selectedOrderIndex + 1}` : 'Delete visit'}</span>
              </div>
            ) : null}

            {canEditTerminalVisit && !creatingNewVisit && isValidForm ? (
              <div
                className={`save-btn copy-btn btn ${isValidForm ? 'valid' : ''}`}
                onClick={() => handleSubmit(null, true)}
              >
                <span>Save and copy</span>
              </div>
            ) : null}

            {(canEditAdministrativeDetails || canEditOrderDetails || canEditTerminalVisit || canEditBollards) && (
              <div className={`save-btn btn ${isValidForm ? 'valid' : ''}`} onClick={() => handleSubmit(null, false)}>
                <span>Save</span>
              </div>
            )}
          </Modal.Buttons>
        </div>
      </React.Fragment>
    </ErrorBoundary>
  )

  /**
   * Check if form fields are correct before updating visit in backend
   */
  function validate(val, isOnExternalQuay) {
    if (!hasBeenValidatedOnce) {
      setHasBeenValidatedOnce(true)
    }
    return validateTerminalVisitForm(val, isOnExternalQuay)
  }

  /**
   * If fiels are validated update visit in backend
   */
  function onValidSubmit(val, copy) {
    const terminalVisit = cloneDeep(val)

    // use ship origin/destination if user hasn't specified either for any of the orders
    terminalVisit.orders.forEach(order => {
      order.origin = order.origin || terminalVisit.ship.origin
      order.destination = order.destination || terminalVisit.ship.destination
    })

    if (terminalVisit._id) {
      // Update the terminal visit (locally && in backend)
      props.updateTerminalVisit(terminalVisit)

      if (copy) {
        const order = findOrderByUuid(terminalVisit, props.selectedOrderId)
        props.copyTerminalVisit(terminalVisit, order, props.config)
      }
    } else {
      // there is no _id field -> we are creating a new visit
      props.insertTerminalVisit(terminalVisit)
    }
  }

  /**
   * Updates visit demurrage and delay orderId when specified order for each is deleted
   * @param {TerminalVisit} terminalVisit
   * @param {object} isOnExternalQuay
   * @returns {object}
   */
  function validateTerminalVisitForm(terminalVisit, isOnExternalQuay) {
    const allProducts = getAllProductsAtQuays(props.config.quays)
    const terminalVisitErrors = validateTerminalVisitProperties(terminalVisit)
    const terminalVisitOrderErrors = validateTerminalVisitOrders(
      terminalVisit,
      allProducts,
      props.config,
      isOnExternalQuay
    )
    const formErrors = { ...terminalVisitErrors, ...terminalVisitOrderErrors }
    return formErrors
  }

  /**
   * Updates visit demurrage and delay orderId when specified order for each is deleted
   * @param {TerminalVisit} visitCopy
   * @param {('demurrages'| 'delays')} prop
   */
  function updateVisitPropsOnDeletedOrder(visitCopy, prop) {
    visitCopy[prop].forEach(p => {
      if (p.orderId === props.selectedOrderId || visitCopy[prop].length === 1) {
        p.orderId = null
      }
    })
  }

  async function addNewOrder() {
    // create a new empty order.
    const newOrder = {
      uuid: uuidV4(),
      administrativeInfo: {},
      amount: 0,
      product: undefined,
      timeIsChained: true,
      parallelGroup: 0
    }

    const visitToUpdate = cloneDeep(values)
    visitToUpdate.orders.push(newOrder)

    // also update the chaining state to include this order.
    const ordersChainCargo = initOrdersChainCargo(visitToUpdate, props.config)

    await setOrdersChainCargo(ordersChainCargo)
    await setAllValues(visitToUpdate)
    handleSelectNewOrder(visitToUpdate.orders.length - 1, visitToUpdate)
  }

  /**
   * When user deletes a terminal visit
   */
  function deleteTerminalVisit() {
    const changedIgnoreImosMessage =
      props.selectedVisit.ignoreIMOS !== values.ignoreIMOS ? (
        <div style={{ marginTop: 15 }}>
          This visit will be removed with IMOS {props.selectedVisit.ignoreIMOS ? 'disabled' : 'enabled'}. <br />
          To apply the changes save the visit before removing it.
        </div>
      ) : !props.selectedVisit.ignoreIMOS ? (
        <div style={{ marginTop: 15 }}>
          This visit is managed by IMOS and will automatically reappear again if it is not deleted in IMOS. <br />
          In order to prevent that, first select `ignore IMOS updates` and save the visit.
        </div>
      ) : null
    if (values.orders.length > 1) {
      // delete only this order from the visit
      confirmDeletePopup({
        itemName: 'this order from the terminal visit',
        onConfirm: () => {
          const updatedVisit = cloneDeep(values)
          const orderToRemove = updatedVisit.orders.find(order => order.uuid === props.selectedOrderId)

          //modal will close after submit so all non existing orders should be removed
          const visits = props.terminalVisits
          const terminalVisit = visits.find(visit => visit._id === props.selectedVisit._id)
          const validOrderToRemove = terminalVisit.orders.find(order => order.uuid === props.selectedOrderId)

          if (!validOrderToRemove) {
            updatedVisit.orders = terminalVisit.orders
            updatedVisit.parallelGroups = terminalVisit.parallelGroups
          } else {
            remove(updatedVisit.orders, order => order.uuid === props.selectedOrderId)

            const parallelGroupsLength = uniqBy(updatedVisit.orders, 'parallelGroup').length

            if (parallelGroupsLength === 1 && updatedVisit.orders.length === 2 && updatedVisit.parallelGroups > 1) {
              updatedVisit.orders.forEach((ord, index) => {
                ord.parallelGroup = index
              })
              updatedVisit.parallelGroups = 2
            } else if (parallelGroupsLength === 1) {
              updatedVisit.orders.forEach(ord => {
                ord.parallelGroup = 0
              })
              updatedVisit.parallelGroups = parallelGroupsLength
            } else if (updatedVisit.parallelGroups !== parallelGroupsLength) {
              updatedVisit.orders.forEach(ord => {
                if (ord.parallelGroup >= orderToRemove.parallelGroup) {
                  ord.parallelGroup -= 1
                }
              })
              updatedVisit.parallelGroups = parallelGroupsLength
            }
          }

          // update orderId for delays and demurrages linked to this order
          updateVisitPropsOnDeletedOrder(updatedVisit, 'delays')
          updateVisitPropsOnDeletedOrder(updatedVisit, 'demurrages')

          props.updateTerminalVisit(updatedVisit)
        },
        alert: true,
        additionalBody: changedIgnoreImosMessage
      })
    } else {
      // delete the whole terminal visit
      confirmDeletePopup({
        itemName: 'this terminal visit',
        onConfirm: () => props.deleteTerminalVisit(values._id),
        alert: true,
        additionalBody: changedIgnoreImosMessage
      })
    }
  }

  /**
   * Find the selected order by uuid
   * Logs a console error when the order is not found
   * @param {TerminalVisit} terminalVisit
   * @returns {Order | null}
   */
  function findSelectedOrder(terminalVisit) {
    const order = findOrderByUuid(terminalVisit, props.selectedOrderId)

    if (!order) {
      console.error('Error: cannot find selected order', {
        selectedOrderId: props.selectedOrderId,
        terminalVisit
      })
    }

    return order
  }

  /**
   * Sets selected ship on the Terminal Visit
   * @param {!object} ship Ship to set on Terminal visit
   * @param {!object} shipDetails Ship details
   */
  async function setShip(ship, shipDetails) {
    if (ship && !isEmpty(ship.mmsi) && values && values.ship.mmsi && isEqual(ship.mmsi, values.ship.mmsi)) {
      return
    }

    const updatedVisit = produce(values, visit => {
      // note: we use shipDetails and we don't use ship because that doesn't have ENI
      visit.ship = shipDetails && !isEmpty(shipDetails) ? toTerminalVisitShip(shipDetails) : {}
    })

    // @ts-ignore
    const savedVisit = await updateShip(updatedVisit)
    // Wait to continue because some details will be edited in the updateShip function

    // Save ship changes in state
    if (isEmpty(shipDetails)) {
      await setShipDetailsForVisit(shipDetails)
      await setPreStatusChange({ status: null, terminalVisit: null })
    } else {
      await setShipDetailsForVisit(shipDetails)
    }

    // Update selected order slack times
    // @ts-ignore
    const order = findSelectedOrder(savedVisit)
    // @ts-ignore
    handleChangeCargo(updateDefaultSlack(order, savedVisit.ship, props.config), savedVisit)
  }

  async function setShipProperty(value, field) {
    const updatedVisit = produce(values, visit => {
      visit.ship[field] = value
    })

    // @ts-ignore
    updateShip(updatedVisit)
  }

  /**
   * Handle having update the ship or ship properties in a terminal visit:
   * update ship length, bollards, cargo end, status, ata/eta/atd
   * @param {TerminalVisit} visit
   */
  async function updateShip(visit) {
    const nameOrMmsiIsChanged = visit.ship.mmsi !== values.ship.mmsi || visit.ship.name !== values.ship.name

    const updatedVisit = produce(visit, updateVisit => {
      if (nameOrMmsiIsChanged) {
        updateVisit.status = isEmpty(visit.ship) ? 'planned' : 'assigned'

        // if ship is removed, we have to fill in the default ship length again
        updateShipLength(updateVisit, props.config)

        const totalDurationDelays = determineTotalDurationDelays(updateVisit)
        updateVisit.orders = updateVisit.orders.map(order => {
          // update cargo end of all orders
          const chainCargo = ordersChainCargo[order.uuid]
          if (chainCargo) {
            updateCargoEnd(order, props.config, totalDurationDelays)
          }

          // update bollards of all orders to match the ship length
          const chainBollard = !inactiveOrdersChainBollard.includes(order.uuid)
          if (chainBollard) {
            if (order.bollardStart) {
              updateBollardEnd(order, updateVisit.ship, props.config)
            } else {
              const quay = props.config.quays.find(quay => quay.name === (values.quayId || props.selectedVisit.quayId))
              const { bollardStart, bollardEnd } = calculateOptimalBollardPositions(
                updateVisit.ship,
                props.config,
                order,
                quay
              )
              order.bollardStart = bollardStart
              order.bollardEnd = bollardEnd
            }
          }

          return order
        })

        // Ship can't have a etaTime, ataTime or atdTime when assigned a new ship or deleted a ship
        clearAtaEtaAtd(updateVisit)
      }
    })

    await setAllValues(updatedVisit)

    return updatedVisit
  }

  /**
   * When the value of a form input field changes
   */
  async function onFormInputChange(value, field) {
    const formFieldsCopy = produce(values, visit => {
      set(visit, field, value)
    })
    await setAllValues(formFieldsCopy)
  }

  function handleChangeQuay(option) {
    onFormInputChange(option, 'quayId')

    // find the quay to move the order to
    const targetQuay = props.config.quays.find(quay => quay.name === option)

    if (!targetQuay) {
      return
    }

    const currentShip = values.ship ? values.ship : {}
    const formFieldsToUpdate = cloneDeep(values)

    formFieldsToUpdate.orders.forEach(order => {
      // update the bollards for the order based on the quay.
      if (targetQuay.role === QUAY_ROLES.normal) {
        const { bollardStart, bollardEnd } = calculateOptimalBollardPositions(
          currentShip,
          props.config,
          order,
          targetQuay
        )
        order.bollardStart = bollardStart
        order.bollardEnd = bollardEnd
      } else {
        order.bollardStart = targetQuay.minBollard
        order.bollardEnd = targetQuay.maxBollard
      }
    })
    handleChainBollard(true, true)

    // update the quay
    formFieldsToUpdate.quayId = option
    setAllValues(formFieldsToUpdate)
  }

  function handleSelectOrder(orderIndex) {
    const selectedOrderId = values.orders[orderIndex].uuid
    props.onSelectVisit(values, selectedOrderId)
  }

  function handleSelectNewOrder(orderIndex, visit) {
    const selectedOrderId = visit.orders[orderIndex].uuid
    props.onSelectVisit(visit, selectedOrderId)
  }

  /**
   * Change the order of parallel orders list
   * @param {Order[]} orders
   * @param {number} orderIndex
   * @param {number} newIndex
   * @param {boolean} enableSwitch
   */
  function handleChangeOrdersOrder(orders, orderIndex, newIndex, enableSwitch) {
    const newlyOrdered = produce(orders, draftOrders => {
      if (enableSwitch) {
        draftOrders[0].parallelGroup = orders[1].parallelGroup
        draftOrders[1].parallelGroup = orders[0].parallelGroup
        reverse(draftOrders)
      } else {
        draftOrders.splice(newIndex, 0, draftOrders.splice(orderIndex, 1)[0])
      }
    })
    saveOrdersInState(newlyOrdered)
  }

  function saveOrdersInState(orders) {
    const formFieldCopy = produce(values, draft => {
      draft.orders = orders
    })
    setAllValues(formFieldCopy)
  }

  function handleChangeOrder(updatedOrder) {
    const updatedVisit = cloneDeep(values)
    const order = updatedVisit.orders.find(order => order.uuid === updatedOrder.uuid)

    if (!order) {
      console.error('updatedOrder', updatedOrder)
      throw new Error('Error: Cannot update order, order not found in terminal visit')
    }

    const visitHasNormalQuay = isVisitInNormalQuay(props.config.quays, updatedVisit)
    const bollardsAreChanged =
      order.bollardStart !== updatedOrder.bollardStart || order.bollardEnd !== updatedOrder.bollardEnd

    if (bollardsAreChanged && visitHasNormalQuay) {
      const closestQuay = getClosestQuayToBollard(
        updatedOrder.bollardStart,
        updatedOrder.bollardEnd,
        props.config.quays
      )
      if (closestQuay) {
        updatedVisit.quayId = closestQuay.name
      }
    }
    Object.assign(order, updatedOrder)

    if (parallelOrders) {
      syncParallelOrders(updatedVisit, updatedOrder)
    }

    setAllValues(updatedVisit)
  }

  function handleChangeCargo(updatedOrder, visit = values) {
    const updatedVisit = cloneDeep(visit)
    const order = updatedVisit.orders.find(order => order.uuid === updatedOrder.uuid)

    if (!order) {
      console.error('updatedOrder', updatedOrder)
      sendMessageToSentry('Cannot update order, order not found in terminal visit', {
        function: 'handleChangeCargo',
        file: 'TerminalVisitForm.js',
        updatedOrder,
        updatedVisit
      })
      toast('Update did not work, this error is reported to the developers. Please try again later')
      return
    }

    Object.assign(order, updatedOrder)

    updateShipLength(updatedVisit, props.config)

    setAllValues(updatedVisit)
  }

  function handleChainCargo(chainCargo) {
    const ordersChainCargoCopy = cloneDeep(ordersChainCargo)
    ordersChainCargoCopy[props.selectedOrderId] = chainCargo
    setOrdersChainCargo(ordersChainCargoCopy)

    const order = findSelectedOrder(values)
    const updatedOrder = cloneDeep(order)

    if (chainCargo) {
      const totalDurationDelays = determineTotalDurationDelays(values)
      updateCargoEnd(updatedOrder, props.config, totalDurationDelays)
    }

    handleChangeOrder(updatedOrder)
  }

  function handleChainBollard(chainBollard, all) {
    const order = findSelectedOrder(values)

    // update state
    if (chainBollard) {
      if (all) {
        // Remove all inactives
        setInactiveOrdersChainBollard([])
      } else {
        // remove current active order
        setInactiveOrdersChainBollard(inactiveOrdersChainBollard.filter(orderId => orderId !== order.uuid))
      }
    } else {
      if (all) {
        // Set all orders to inactive
        const orderIds = produce(values.orders, orders => {
          orders.map(order => order.uuid)
        })
        setInactiveOrdersChainBollard(orderIds)
      } else {
        const inactiveOrdersChainBollardCopy = produce(inactiveOrdersChainBollard, draftState => {
          draftState.push(order.uuid)
        })
        setInactiveOrdersChainBollard(inactiveOrdersChainBollardCopy)
      }
    }

    const ship = values.ship
    const config = props.config
    if (all === true) {
      const chainedOrders = values.orders.map(order => {
        return chainSingleOrder(order)
      })
      const updatedVisit = produce(values, visitDraft => {
        visitDraft.orders = chainedOrders
      })

      setAllValues(updatedVisit)
    } else {
      const updatedOrder = chainSingleOrder(order)

      handleChangeOrder(updatedOrder)
    }

    function chainSingleOrder(order) {
      return produce(order, orderDraft => {
        if (chainBollard) {
          updateBollardEnd(orderDraft, ship, config)
        }
      })
    }
  }

  function toggleIsParallelVisit() {
    const updatedVisit = produce(values, draftVisit => {
      if (draftVisit.parallelGroups === 1) {
        draftVisit.parallelGroups = 2
        draftVisit.orders[1].parallelGroup = 1

        // sync the bollards of alle the orders from the visit
        const order = draftVisit.orders.find(order => order.uuid === props.selectedOrderId)
        syncParallelOrders(draftVisit, order)

        return draftVisit
      }

      draftVisit.parallelGroups = 1
      draftVisit.orders.forEach(order => (order.parallelGroup = 0))
      return draftVisit
    })

    setAllValues(updatedVisit)
  }

  /**
   * When the status changes, in some cases delete the ataTime and atdTime
   */
  function onStatusInputChange(value, field) {
    // User changed the status for the first time
    if (!preStatusChange.status) {
      handleFormChangeAfterSelectingNewStatus(value, field)

      setPreStatusChange({
        status: values.status,
        terminalVisit: cloneDeep(values)
      })
    } else {
      // the old status is selected again! Set certain fields back again
      if (value === preStatusChange.status) {
        handleFormChangeAfterReselectingStatus(value, field, preStatusChange.terminalVisit)
      } else {
        // A different status is selected, fill the copy object with new values when possible
        const currentFormFields = cloneDeep(values)
        const copyFormFields = cloneDeep(preStatusChange.terminalVisit)

        // check the current form fields if there fields are set, if true, use them in the copy
        if (currentFormFields.ship) {
          // Current form has a mmsi (is a detailed ship) so accept the whole ship object as new
          if (currentFormFields.ship.mmsi) {
            copyFormFields.ship = currentFormFields.ship
          } else {
            if (!copyFormFields.ship || !copyFormFields.ship.mmsi) {
              copyFormFields.ship = currentFormFields.ship
            }
          }
        }

        if (currentFormFields.ataTime) {
          copyFormFields.ataTime = currentFormFields.ataTime
        }

        if (currentFormFields.atdTime) {
          copyFormFields.atdTime = currentFormFields.atdTime
        }

        handleFormChangeAfterReselectingStatus(value, field, copyFormFields)

        setPreStatusChange({
          status: values.status,
          terminalVisit: copyFormFields
        })
      }
    }
  }

  function handleFormChangeAfterSelectingNewStatus(value, field) {
    const formFieldsCopy = cloneDeep(values)
    set(formFieldsCopy, field, value)

    if (value === 'planned' || value === 'assigned') {
      formFieldsCopy.ataTime = null
      formFieldsCopy.atdTime = null
    }
    if (value === 'nearby') {
      formFieldsCopy.ataTime = null
      formFieldsCopy.atdTime = null
    }
    if (value === 'moored') {
      formFieldsCopy.ataTime = formFieldsCopy.ataTime ? formFieldsCopy.ataTime : null
      formFieldsCopy.atdTime = null
    }

    if (value === 'planned') {
      // delete ship
      setShip(null, null)
      formFieldsCopy.ship = null

      // in case we have one order, determine the default ship length from the selected product
      if (formFieldsCopy.orders.length === 1) {
        const product = first(formFieldsCopy.orders).product

        if (product) {
          formFieldsCopy.ship = {
            length: determineShipLength(product, props.config)
          }
        }
      }
    }

    setAllValues(formFieldsCopy)
  }

  function handleFormChangeAfterReselectingStatus(value, field, previousForm) {
    const formFieldsCopy = cloneDeep(values)
    set(formFieldsCopy, field, value)

    if (value === 'planned') {
      // delete ship
      setShip(null, null)

      formFieldsCopy.ship = null
      formFieldsCopy.ataTime = null
      formFieldsCopy.atdTime = null

      // in case we have one order, determine the default ship length from the selected product
      if (formFieldsCopy.orders.length === 1) {
        const product = first(formFieldsCopy.orders).product

        if (product) {
          formFieldsCopy.ship = {
            length: determineShipLength(product, props.config)
          }
        }
      }
    } else if (value === 'assigned') {
      formFieldsCopy.ship = previousForm.ship
      formFieldsCopy.ataTime = null
      formFieldsCopy.atdTime = null
    } else if (value === 'nearby') {
      formFieldsCopy.ship = previousForm.ship
      formFieldsCopy.ataTime = null
      formFieldsCopy.atdTime = null
    } else if (value === 'moored') {
      formFieldsCopy.ship = previousForm.ship
      formFieldsCopy.ataTime = previousForm.ataTime
      formFieldsCopy.atdTime = null
    } else if (value === 'sailed') {
      formFieldsCopy.ship = previousForm.ship
      formFieldsCopy.ataTime = previousForm.ataTime
      formFieldsCopy.atdTime = previousForm.atdTime
    }

    setAllValues(formFieldsCopy)
  }

  function handleDemurrageInputChanged(value, field, index) {
    const terminalVisit = cloneDeep(values)

    let savedValue

    switch (field) {
      case 'demurrageInCents':
        // demurrage charges are displayed in basic monetary unit, but handled as cents
        savedValue = value * 100
        break
      case 'allowedDuration':
        // allowedDuration displayed in hours, but handled as milliseconds
        savedValue = value * 60 * 60 * 1000
        break
      default:
        savedValue = value
    }

    terminalVisit.demurrages[index][field] = savedValue

    setAllValues(terminalVisit)
  }

  function handleDelayInputChange(value, field, index) {
    const terminalVisit = cloneDeep(values)
    terminalVisit.delays[index][field] = value

    const config = props.config
    const totalDurationDelays = determineTotalDurationDelays(terminalVisit)

    terminalVisit.orders.forEach(order => {
      const chainCargo = ordersChainCargo[order.uuid]
      if (chainCargo) {
        updateCargoEnd(order, config, totalDurationDelays)
      }
    })

    setAllValues(terminalVisit)
  }

  function handleAddDelay() {
    const formFieldsCopy = cloneDeep(values)

    if (!formFieldsCopy.delays) {
      formFieldsCopy.delays = [
        {
          delayType: 'RAIN'
        }
      ]
    } else {
      formFieldsCopy.delays.push({
        delayType: 'RAIN'
      })
    }

    setAllValues(formFieldsCopy)
  }

  function isVisitIncurringDemurrage() {
    const visit = values
    const orders = visit.orders
    const demurrages = visit.demurrages
    let incurringDemurrageForSingleOrder = false

    if (!orders || !demurrages || isEmpty(demurrages) || isEmpty(orders)) {
      return false
    }

    // First check if a single order is incurring demurrage because of a specific demurrage rule for that order.
    demurrages.forEach(demurrage => {
      if (demurrage.orderId) {
        const orderWithDemurrageRule = orders.find(order => order.uuid === demurrage.orderId)
        const incurringDemurrage = isOrderIncurringDemurrage(orderWithDemurrageRule, demurrage)
        if (incurringDemurrage) {
          incurringDemurrageForSingleOrder = true
        }
      }
    })

    if (incurringDemurrageForSingleOrder) {
      return true
    }
    // Secondly check if the visit is incurring demurrage based on demurrage rules put in place for the entire visit.
    // Get all the demurrages not linked to a specific order
    const demurragesForEntireVisit = demurrages.filter(
      demurrage =>
        demurrage.orderId === undefined || demurrage.orderId === 'Terminal Visit' || demurrage.orderId === null
    )

    // loops through all the orders to combine all the cargo start / end values
    // we then use this array to get the maximum and minimum values to calculate the visits duration
    const timestampsStart = orders.flatMap(order => {
      return [order.cargoStart]
    })
    const timestampsEnd = orders.flatMap(order => {
      return [order.cargoEnd]
    })

    // calculate the duration of the visit by using the max and min value of the orders
    const visitDuration = Math.max(...timestampsEnd) - Math.min(...timestampsStart)

    // go through all the demurrages ( ones without orderId ) and add up the durations of the demurrages
    const demurrageDuration = demurragesForEntireVisit.reduce((accumulator, currentValue) => {
      if (currentValue.allowedDuration) {
        return accumulator + currentValue.allowedDuration
      } else {
        return accumulator
      }
    }, 0)

    if (demurrageDuration > 0) {
      // return if the visit duration is bigger than the demurrage duration.
      return visitDuration > demurrageDuration
    } else {
      // returns false if the demurrage is 0 -> happens when there's a empty demurrage rule being created by the user.
      return false
    }
  }

  function isOrderIncurringDemurrage(order, demurrageRuleForOrder) {
    if (!order || !demurrageRuleForOrder) {
      return false
    }

    const demurrageDuration = demurrageRuleForOrder.allowedDuration
    const orderDuration = order.cargoEnd - order.cargoStart

    return orderDuration > demurrageDuration
  }

  function handleDeleteDemurrage(index) {
    const formFieldsCopy = produce(values, visit => {
      visit.demurrages.splice(index, 1)
    })
    setAllValues(formFieldsCopy)
  }

  function handleDeleteDelay(index) {
    const formFieldsCopy = produce(values, visit => {
      visit.delays.splice(index, 1)
    })
    setAllValues(formFieldsCopy)
  }
}

/**
 * convert ship details coming from the platform into a TerminalVisitShip
 */
function toTerminalVisitShip(shipDetails) {
  if (!shipDetails) {
    return null
  }

  const shipName = shipDetails.name ? removeSpecialCharsFromShipName(shipDetails.name) : null

  const transponder = shipDetails.positionOfTransponder
  const shipLength = transponder != null ? transponder.distanceToBow + transponder.distanceToStern : null
  const shipWidth = transponder != null ? transponder.distanceToPort + transponder.distanceToStarboard : null

  return {
    mmsi: shipDetails.mmsi,
    imo: shipDetails.imoNumber,
    eni: shipDetails.eni,
    length: shipLength,
    width: shipWidth,
    name: shipName,
    type: shipDetails.imoNumber ? 'sea-ship' : 'barge'
  }
}

/**
 * Removes all special characters from given string
 */
function removeSpecialCharsFromShipName(str) {
  return str.replace(/[^\w\s]/gi, '')
}

TerminalVisitForm.propTypes = {
  terminalVisits: PropTypes.array,
  quay: PropTypes.string,
  selectedVisit: PropTypes.object.isRequired,
  selectedOrderId: PropTypes.string, // optional, for a new visit it's undefined
  onSelectVisit: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  updateTerminalVisit: PropTypes.func.isRequired,
  copyTerminalVisit: PropTypes.func.isRequired,
  insertTerminalVisit: PropTypes.func.isRequired,
  deleteTerminalVisit: PropTypes.func.isRequired,
  getShipDetailsByMmsi: PropTypes.func.isRequired,
  authToken: PropTypes.string,
  customFetch: PropTypes.func.isRequired,
  userRoles: PropTypes.array.isRequired
}

export default React.memo(TerminalVisitForm)
